<template>
  <div
    id="app"
    class="h-100"
    :class="[skinClasses]"
  >
    <!-- Alerta de Nova Versão Disponível -->
    <transition name="fade">
      <div
        v-if="hasUpdate"
        class="app-update-banner"
      >
        <div class="d-flex align-items-center justify-content-between">
          <div class="d-flex align-items-center mr-2">
            <feather-icon
              icon="RefreshCwIcon"
              size="20"
              class="mr-1 text-primary spin-icon"
            />
            <span class="font-weight-bold">Uma nova versão do sistema está disponível!</span>
          </div>
          <div>
            <b-button
              variant="primary"
              size="sm"
              @click="reloadApp"
            >
              Atualizar para nova versão
            </b-button>
          </div>
        </div>
      </div>
    </transition>

    <component :is="layout">
      <router-view />
    </component>

  </div>
</template>

<script>

// This will be populated in `beforeCreate` hook
import { $themeColors, $themeBreakpoints, $themeConfig } from '@themeConfig'
import { provideToast } from 'vue-toastification/composition'
import { watch } from '@vue/composition-api'
import useAppConfig from '@core/app-config/useAppConfig'
import echoEvents from "@/echoEvents";
import { useWindowSize, useCssVar } from '@vueuse/core'
import Echo from "laravel-echo";
import store from '@/store'
import Cookie from "js-cookie";
import { BButton } from 'bootstrap-vue'
import { checkAppVersion, onNewVersionAvailable, reloadPage } from '@/services/versionCheck'
const LayoutVertical = () => import('@/layouts/vertical/LayoutVertical.vue')
const LayoutHorizontal = () => import('@/layouts/horizontal/LayoutHorizontal.vue')
const LayoutFull = () => import('@/layouts/full/LayoutFull.vue')

export default {
  components: {
    BButton,

    // Layouts
    LayoutHorizontal,
    LayoutVertical,
    LayoutFull,

  },
  data() {
    return {
      hasUpdate: false,
      versionCheckInterval: null,
    }
  },
  watch: {
    $route(val) {
      if(window.Echo === undefined) {
        window.io = require('socket.io-client')
        let bearerToken = Cookie.get('token')

        const optionsEcho = {
          broadcaster: 'socket.io',
          host: window.baseUrl + ':8443',
          auth: {
            headers: {
              Authorization: 'Bearer ' + bearerToken,
            }
          }
        }
        if(window.location.pathname !== '/login') {
          window.Echo = new Echo(optionsEcho)
          echoEvents(this.$store)
        }
      }
    }
  },
  // ! We can move this computed: layout & contentLayoutType once we get to use Vue 3
  // Currently, router.currentRoute is not reactive and doesn't trigger any change
  computed: {
    layout() {
      if (this.$route.meta.layout === 'full') return 'layout-full'
      return `layout-${this.contentLayoutType}`
    },
    contentLayoutType() {
      return this.$store.state.appConfig.layout.type
    },
  },
  mounted() {
    if(!this.$store.state.laboratorios.laboratorios) {
      this.$store.dispatch('laboratorios/listaLaboratorios')
    }

    // Version update check
    onNewVersionAvailable(() => {
      this.hasUpdate = true
    })
    checkAppVersion()

    // Check periodically every 5 minutes
    this.versionCheckInterval = setInterval(() => {
      checkAppVersion()
    }, 5 * 60 * 1000)

    window.addEventListener('focus', this.checkOnFocus)
  },
  beforeDestroy() {
    if (this.versionCheckInterval) {
      clearInterval(this.versionCheckInterval)
    }
    window.removeEventListener('focus', this.checkOnFocus)
  },
  methods: {
    checkOnFocus() {
      checkAppVersion()
    },
    reloadApp() {
      reloadPage()
    },
  },
  beforeCreate() {
    // Set colors in theme
    const colors = ['primary', 'secondary', 'success', 'info', 'warning', 'danger', 'light', 'dark']

    // eslint-disable-next-line no-plusplus
    for (let i = 0, len = colors.length; i < len; i++) {
      $themeColors[colors[i]] = useCssVar(`--${colors[i]}`, document.documentElement).value.trim()
    }

    // Set Theme Breakpoints
    const breakpoints = ['xs', 'sm', 'md', 'lg', 'xl']

    // eslint-disable-next-line no-plusplus
    for (let i = 0, len = breakpoints.length; i < len; i++) {
      $themeBreakpoints[breakpoints[i]] = Number(useCssVar(`--breakpoint-${breakpoints[i]}`, document.documentElement).value.slice(0, -2))
    }

    // Set RTL
    const { isRTL } = $themeConfig.layout
    document.documentElement.setAttribute('dir', isRTL ? 'rtl' : 'ltr')
  },
  setup() {
    const { skin, skinClasses } = useAppConfig()

    // If skin is dark when initialized => Add class to body
    if (skin.value === 'dark') document.body.classList.add('dark-layout')

    // Provide toast for Composition API usage
    // This for those apps/components which uses composition API
    // Demos will still use Options API for ease
    provideToast({
      hideProgressBar: true,
      closeOnClick: false,
      closeButton: false,
      icon: false,
      timeout: 3000,
      transition: 'Vue-Toastification__fade',
    })

    // Set Window Width in store
    store.commit('app/UPDATE_WINDOW_WIDTH', window.innerWidth)
    const { width: windowWidth } = useWindowSize()
    watch(windowWidth, val => {
      store.commit('app/UPDATE_WINDOW_WIDTH', val)
    })

    return {
      skinClasses,
    }
  },
}
</script>
<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';

.app-update-banner {
  position: fixed;
  bottom: 24px;
  right: 24px;
  z-index: 999999;
  background: #ffffff;
  color: #333333;
  padding: 16px 20px;
  border-radius: 8px;
  box-shadow: 0 4px 24px 0 rgba(34, 41, 47, 0.24);
  border-left: 4px solid #7367f0;
  max-width: 480px;

  .spin-icon {
    animation: spin 3s linear infinite;
  }
}

.dark-layout .app-update-banner {
  background: #283046;
  color: #d0d2d6;
  box-shadow: 0 4px 24px 0 rgba(0, 0, 0, 0.4);
}

@keyframes spin {
  100% {
    transform: rotate(360deg);
  }
}
</style>
